<template>
  <div class='automated-remediation-settings'>
    <div class="automated-remediation-settings-header">
      <div class="automated-remediation-settings-header__title">
        {{$t('automated.remediation.settings.title')}}
      </div>
      <div class="automated-remediation-settings-header__subtitle">
        {{$t('employee.leaked.credentials')}}
      </div>
    </div>
    <div class='automated-remediation-settings-add-email'>
      <div class='automated-remediation-settings-add-email__title'>{{ $t('tenant.settings.distribution.list') }}</div>
      <div class='automated-remediation-settings-add-email__list'>
        <v-chip
          label
          close
          pill
          small
          text-color='#2459d5'
          class='email'
          v-for='(emailKey, index) in emailList'
          :key='index'
          close-icon='close'
          @click:close='removeEmailFromList(emailKey)'
        >
          {{ emailKey }}
        </v-chip>
      </div>
      <div class="automated-remediation-settings-add-email__btn">
        <v-btn @click="toggleShowAddEmailPopup" class="add-email-btn">
          {{$t('add.new.email.popup.button')}}
        </v-btn>
      </div>
      <div class="automated-remediation-settings-add-email__notify">
        <div class="automated-remediation-settings-add-email__notify__value">
          <CustomCheckbox
            color="cold-marine"
            :value="isNotifyUser"
            size="s"
            @input="updateNotifyUser"
          />
          <div class="automated-remediation-settings-add-email__notify__value__text">
            {{ $t('notify.user') }}
          </div>
        </div>
        <div class='automated-remediation-settings-add-email__notify__message' v-if='isNotifyUser'>
          <div class='automated-remediation-settings-add-email__notify__message__title'>
            {{$t('custom.message')}}
          </div>
          <textarea
            v-model="userMessage"
            :placeholder="$t('custom.user.message')"
            class='automated-remediation-settings-add-email__notify__message__text'
            @focus='isEdit = true'
          ></textarea>
          <div class='automated-remediation-settings-add-email__notify__message__buttons' v-if='isEdit'>
            <v-btn @click="saveUserMessage" :disabled='!isSave' class="btn-secondary save">
              {{$t('save')}}
            </v-btn>
            <v-btn @click="isEdit = false" class="btn-tertiary cancel">
              {{$t('cancel')}}
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <add-email-to-distribution-list-popup
      :visiblePopup="showAddEmailPopup"
      @closeAddEmailPopup="toggleShowAddEmailPopup"
      @saveAddEmail="addEmailToList"
    >
    </add-email-to-distribution-list-popup>
    <error-message :errorText="errorMessage" @close="errorMessage = ''" />
  </div>
</template>

<script>
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import { mapGetters } from 'vuex';
import { ADMIN_SYSTEM_SETTINGS, GET_SYSTEM_SETTINGS } from '@/store/user/actionTypes';
import AddEmailToDistributionListPopup from '@/components/popup/AddEmailToDistributionListPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';

export default {
  name: 'AutomatedRemediationSettings',
  components: { CustomCheckbox, AddEmailToDistributionListPopup, ErrorMessage },
  data() {
    return {
      userMessage: '',
      isEdit: false,
      isSave: false,
      showAddEmailPopup: false,
      errorMessage: '',
    };
  },
  async created() {
    try {
      await this.$store.dispatch(GET_SYSTEM_SETTINGS);
    } catch (e) {
      this.errorMessage = e.message;
    }
    this.userMessage = this.customMessage;
  },
  watch: {
    userMessage(newVal) {
      this.isSave = newVal !== this.customMessage;
    },
  },
  computed: {
    ...mapGetters(['systemSettings']),
    customMessage() {
      return this.systemSettings?.settingsMetaData?.userMessage || '';
    },
    emailList() {
      return this.systemSettings?.settingsMetaData?.list || [];
    },
    isNotifyUser() {
      return this.systemSettings?.settingsMetaData?.notifyUser;
    },
  },
  methods: {
    async updateNotifyUser() {
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, { notifyUser: !this.isNotifyUser });
        if (!this.isNotifyUser) {
          this.userMessage = '';
        }
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async saveUserMessage() {
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, { userMessage: this.userMessage });
      } catch (e) {
        this.errorMessage = e.message;
      }
      this.isEdit = false;
      this.isSave = false;
    },
    toggleShowAddEmailPopup() {
      this.showAddEmailPopup = !this.showAddEmailPopup;
    },
    async removeEmailFromList(email) {
      const emails = this.systemSettings.settingsMetaData.list.filter(item => item !== email);
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, { emails, removeEmail: true });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
    async addEmailToList(emails) {
      this.toggleShowAddEmailPopup();
      const formattedEmails = emails.map(email => email.email);
      try {
        await this.$store.dispatch(ADMIN_SYSTEM_SETTINGS, { emails: formattedEmails });
      } catch (e) {
        this.errorMessage = e.message;
      }
    },
  },
};
</script>

<style lang='scss' scoped>
@import "@/assets/styles/variables";
.automated-remediation-settings {
  height: 100%;
  margin-left: 3%;
  padding: 0px 86px 0px 32px;
  display: flex;
  flex-direction:column;
  overflow-y: scroll;
  overflow-x: hidden;
  .automated-remediation-settings-header {
    margin-bottom: 1%;
    margin-top: 2%;
    &__title, &__subtitle {
      color: var(--text-primary);
      font-family: $default-application-font-bold !important;
      font-size: 20px !important;
      font-weight: bold;
      line-height: 20px;
      letter-spacing: 0 !important;
      margin-bottom: 20px;
    }
    &__subtitle {
      font-size: 18px !important;
      color: var(--text-secondary);
    }
  }
  .automated-remediation-settings-add-email {
    margin-bottom: 2%;
    .text-field-container {
      margin-top: -20px;
      .input-description {
        min-width: 100%;
        justify-self: left;
      }
    }
    &__list {
      .email {
        margin: 10px 5px 5px 0;
        background-color: var(--bg-tertiary) !important;
        color: var(--text-primary) !important;
        font-family: $default-application-font-regular;
      }
    }
    &__title {
      text-align: left;
      color: var(--text-secondary);
      font-family: $default-application-font-regular !important;
      font-size: 12px !important;
      line-height: 20px;
      letter-spacing: 1px !important;
      text-transform: uppercase;
    }
    &__btn {
      margin-top: 10px;
      .add-email-btn {
        background-color: var(--brand-primary) !important;
        border-radius: 4px !important;
        box-shadow: none !important;
        color: #FFFFFF !important;
        font-family: Inter-Medium, sans-serif;
        font-size: 12px;
        height: 28px;
        min-width: 100px !important;
        letter-spacing: 1.1px;
        line-height: 16px;
        width: auto;
        padding: 0 16px !important;
      }
    }

    &__notify {
      margin-top: 30px;
      &__value {
        display: flex;
        &__text {
          font-family: $default-application-font-regular !important;
          font-size: 14px !important;
          color: var(--text-primary);
          margin-top: -2px;
          margin-left: -3px;
        }
      }

      &__message {
        margin-top: 15px;
        &__title {
          font-family: $default-application-font-regular !important;
          font-size: 12px !important;
          color: var(--text-secondary);
          text-transform: uppercase;
        }
        textarea {
          min-width: 700px;
          min-height: 100px;
          margin-top: 5px;
          border: 1px solid var(--border-primary);
          background-color: var(--bg-primary);
          color: var(--text-primary);
          padding: 8px;
          border-radius: 4px;
          &:focus {
            outline: none !important;
            border-color: var(--brand-primary);
          }
        }
        &__buttons {
          margin-top: 10px;
          .save {
            margin-right: 20px;
          }
          .cancel {
            background-color: transparent !important;
            color: var(--text-primary) !important;
            border: 1px solid var(--border-secondary) !important;
          }
        }
      }
    }
  }
}
</style>
